class AnnouncementBar {
  constructor(section) {
    this.section = section;
    this.announcementBar = section.querySelector(".announcement-bar");

    this.currentScrollTop = 0;

    if (this.announcementBar) {
      this.initRotator();
    }

    /* The handler queries the DOM and reads offsetHeight, so it is coalesced
       to one run per frame and registered passive - it never calls
       preventDefault, and a non-passive scroll listener blocks the
       compositor from scrolling until JS has finished. */
    this.scrollTicking = false;
    this.onScrollHandler = () => {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.scrollTicking = false;
        this.onScroll();
      });
    };
    window.addEventListener("scroll", this.onScrollHandler, { passive: true });
  }

  // Plain class-swap rotator. No carousel library, no cloned slides, no
  // transform on a shared track — the three things that broke this twice.
  //
  // Fail-open: the first message already carries `is-active` from Liquid, so if
  // this never runs the bar shows one message rather than none.
  initRotator() {
    const slides = this.announcementBar.querySelectorAll(
      "[data-announcement-slide]"
    );
    if (slides.length < 2) return; // nothing to rotate between

    const delay = Math.max(1500, this.announcementBar.dataset.delay * 1000);
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return; // leave the first message showing, permanently

    // Keep the CSS fade in step with the section's speed setting. Without
    // this the transition duration is hardcoded in CSS and silently drifts out
    // of sync whenever someone changes the setting in the theme editor.
    const speed = parseFloat(this.announcementBar.dataset.speed);
    if (speed > 0) {
      this.announcementBar.style.setProperty("--announcement-fade", speed + "s");
    }

    let index = 0;
    let timer = null;

    const show = (next) => {
      slides[index].classList.remove("is-active");
      index = next;
      slides[index].classList.add("is-active");
    };

    const tick = () => show((index + 1) % slides.length);

    const start = () => {
      // Guard against ever stacking two intervals, which is how a ticker ends
      // up racing and then appearing to stop.
      if (timer) clearInterval(timer);
      timer = setInterval(tick, delay);
    };
    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };

    start();

    // Pause on hover so a message can actually be read, and — unlike Swiper's
    // pauseOnMouseEnter — always resume, including when the pointer leaves via
    // a scroll or a tab switch rather than a clean mouseleave.
    this.announcementBar.addEventListener("mouseenter", stop);
    this.announcementBar.addEventListener("mouseleave", start);
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });
  }

  onScroll() {
    // The bar can legitimately be absent (section removed in the editor). This
    // used to dereference it unguarded and throw on every scroll event.
    if (!this.announcementBar) return;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const stickyType = this.announcementBar.dataset.stickyType;
    const headerWrapper = document.querySelector(".shopify-section-header");
    const stickyHeader = headerWrapper.querySelector("sticky-header");

    const headerStickyMode = stickyHeader
      ? stickyHeader.dataset.stickyType
      : null;

    if (stickyType === "always") {
      this.section.classList.add("is-visible");
      if (scrollTop > this.currentScrollTop) {
        this.section.classList.remove("info-bar");
        if (headerStickyMode != null) {
          this.section.classList.add("scroll-down");
          this.section.classList.remove("scroll-up");
        } else {
          this.section.classList.add("scroll-down-top");
          this.section.classList.remove("scroll-up-top");
        }
      } else {
        if (window.scrollY <= headerWrapper.offsetHeight) {
          this.section.classList.add("info-bar");
        } else {
          this.section.classList.remove("info-bar");
        }
        if (headerStickyMode != null) {
          if (headerWrapper) {
            if (window.scrollY <= headerWrapper.offsetHeight) {
              headerWrapper.classList.add("set-info-top");
            } else {
              headerWrapper.classList.remove("set-info-top");
            }
          }
          this.section.classList.add("scroll-up");
          this.section.classList.remove("scroll-down");
          if (headerStickyMode === "always") {
            this.section.classList.add("show-header");
          } else {
            this.section.classList.remove("show-header");
          }
        } else {
          if (headerWrapper) {
            if (window.scrollY <= headerWrapper.offsetHeight) {
              this.section.classList.add("top-zero");
              headerWrapper.classList.add("is-first");
            } else {
              this.section.classList.remove("top-zero");
              headerWrapper.classList.remove("is-first");
            }
          }
          this.section.classList.add("scroll-up-top");
          this.section.classList.remove("scroll-down-top");
        }

        this.reveal();
      }
    }

    if (stickyType === "on-scroll-up") {
      if (scrollTop > this.currentScrollTop) {
        this.section.classList.remove("info-bar");
        if (headerStickyMode != null) {
          this.section.classList.add("scroll-down");
          this.section.classList.remove("scroll-up");
        } else {
          this.section.classList.add("scroll-down-top");
          this.section.classList.remove("scroll-up-top");
        }
        this.hide();
      } else {
        if (window.scrollY <= headerWrapper.offsetHeight) {
          this.section.classList.add("info-bar");
        } else {
          this.section.classList.remove("info-bar");
        }
        if (headerStickyMode != null) {
          if (headerWrapper) {
            if (window.scrollY <= headerWrapper.offsetHeight) {
              headerWrapper.classList.add("set-info-top");
            } else {
              headerWrapper.classList.remove("set-info-top");
            }
          }
          this.section.classList.add("scroll-up");
          this.section.classList.remove("scroll-down");
        } else {
          if (headerWrapper) {
            if (window.scrollY <= headerWrapper.offsetHeight) {
              this.section.classList.add("top-zero");
              headerWrapper.classList.add("is-first");
            } else {
              this.section.classList.remove("top-zero");
              headerWrapper.classList.remove("is-first");
            }
          }
          this.section.classList.add("scroll-up-top");
          this.section.classList.remove("scroll-down-top");
        }
        this.reveal();
      }
    }

    this.currentScrollTop = scrollTop;
  }

  updateHeight() {
    const height = this.section.offsetHeight;
    document.documentElement.style.setProperty(
      "--announcement-height",
      `${height}px`
    );
  }

  reveal() {
    this.updateHeight();
    this.section.classList.add("is-visible");
    this.section.classList.remove("is-hidden");
  }

  hide() {
    this.section.classList.add("is-hidden");
    this.section.classList.remove("is-visible");
  }

  reset() {
    this.section.classList.remove("is-hidden", "is-visible");
  }
}

// --- INITIALIZATION ---
const initAnnouncement = (container) => {
  const section = container.querySelector(".section-announcement");
  if (section && !section.dataset.initialized) {
    new AnnouncementBar(section);
    section.dataset.initialized = true;
  }
};

initAnnouncement(document);

document.addEventListener("shopify:section:load", (event) => {
  initAnnouncement(event.target);
});
